import React, { useState, useEffect } from "react";
import { Grid, Button } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { getJobOrder } from "../API/jobOrderAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import TransmittalNotesCard from "./FormComponents/TransmittalNotesCard";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function ViewTransmittalForm() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [jobOrder, setJobOrder] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPayrollReports = searchParams.get("from") === "payroll-reports";
  const returnTo = searchParams.get("returnTo");

  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes("operations_technician");

  useEffect(() => {
    if (!id) return;

    let isActive = true;

    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        if (isActive) setJobOrder(data || {});
      } catch (err) {
        if (isActive) {
          setErrorMessage(
            `Error fetching job order${err?.message ? `: ${err.message}` : ""}`
          );
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchJobOrder();

    return () => {
      isActive = false;
    };
  }, [id]);

  if (!jobOrder) return null;

  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const effectiveEndTime = getEffectiveEndTime(jobOrder);

  const displayJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
  };

  const pageOneJobOrder = {
    ...displayJobOrder,
    client: displayJobOrder?.client || {},
  };

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    transmittal_notes: "Transmittal Notes Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) => {
    return (
      jobOrder?.[`${stepKey}_completed_at`] ||
      latestStepLogMap?.[stepKey] ||
      ""
    );
  };

  const documentLinks = {
    ...buildGeneratedDocumentLinks(jobOrder?.id, {
      includeReport: false,
      includeCertificate: false,
      includeReceipt: true,
    }),
    ...(jobOrder?.receipts_url && {
      receipts_url: jobOrder.receipts_url,
    }),
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="View Transmittal Form"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        <PageOneReviewSection
          jobOrder={pageOneJobOrder}
          client={jobOrder?.client || {}}
          selectedJobTypes={displayedJobTypes}
          selectedJobDescriptions={displayedJobDescriptions}
          jobOrderParameters={{ status: jobOrder?.status || "" }}
          errors={{}}
          onStatusChange={() => {}}
          showStatus={true}
          readOnly={true}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.review_job_details}
              value={getStepTimestamp("review_job_details")}
              colorSet="info"
            />
          </Grid>
        )}

        <TransmittalNotesCard
          notes={jobOrder?.transmittal_notes || ""}
          readOnly={true}
          title="Transmittal Notes"
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.transmittal_notes}
              value={getStepTimestamp("transmittal_notes")}
              colorSet="info"
            />
          </Grid>
        )}

        <TechnicianOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.technician_confirmation}
              value={getStepTimestamp("technician_confirmation")}
              colorSet="info"
            />
          </Grid>
        )}

        <ClientOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        <DocumentLinksCard
          title="Generated Documents"
          links={documentLinks}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Report Submitted At"
              value={jobOrder?.report_submitted_at}
              colorSet="warning"
            />
          </Grid>
        )}

        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                window.location.href = returnTo;
              } else if (fromPayrollReports) {
                navigate("/payroll-reports");
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            {fromPayrollReports ? "Back to Payroll Reports" : "Cancel"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default ViewTransmittalForm;